import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Sparkles, 
  Zap, 
  Settings,
  Database,
  Activity,
  TrendingUp,
  Users,
  BarChart3,
  PieChart,
  LineChart,
  Globe,
  Calendar,
  Plus,
  Loader2,
  AlertCircle,
  Upload,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { DataSource } from '@shared'
import { useQuickChartCreation } from '@/hooks/useQuickChartCreation'
import { useAiChartAssistant } from '@/hooks/useAiChartAssistant'
import UniversalChartRenderer from './UniversalChartRenderer'
import { normalizeChartData } from '@/lib/chartDataNormalizer'
import { useCustomPalettes, useActiveDefaultPaletteId } from '@/contexts/ChartPreferencesContext'
import { useTheme } from '@/lib/themeUtils'
import { useManualChartCreator } from '@/hooks/useManualChartCreator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ChartData as LocalChartData, createStandardChartObject, addChartAtom } from '@/store/chartAtoms'
import { DataTableViewer } from '@/components/ui/DataTableViewer'
import { useToast } from '@/hooks/use-toast'
import { useAtom } from 'jotai'

type CreationMode = 'quick' | 'ai' | 'manual' | 'upload'

interface UnifiedAIChartCreatorProps {
  isOpen: boolean
  onClose: () => void
  dataSources: DataSource[]
  onChartCreated?: () => void
}

interface QuickActionTemplate {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  chartType: 'line' | 'bar' | 'pie'
  dataSourceType: 'google-analytics' | 'firestore' | 'any'
  metrics: string[]
  dimensions: string[]
  dateRange: string
  badge: string
}

// Quick action templates based on connected data sources
const quickActionTemplates: QuickActionTemplate[] = [
  {
    id: 'website-traffic',
    title: 'Website Traffic Trends',
    description: 'Show visitor trends over the last 30 days',
    icon: <TrendingUp className="h-6 w-6 text-blue-500" />,
    chartType: 'line',
    dataSourceType: 'google-analytics',
    metrics: ['activeUsers'],
    dimensions: ['date'],
    dateRange: 'last 30 days',
    badge: 'Google Analytics'
  },
  {
    id: 'device-breakdown',
    title: 'Users by Device',
    description: 'Pie chart showing device category breakdown',
    icon: <PieChart className="h-6 w-6 text-green-500" />,
    chartType: 'pie',
    dataSourceType: 'google-analytics',
    metrics: ['activeUsers'],
    dimensions: ['deviceCategory'],
    dateRange: 'last 30 days',
    badge: 'Google Analytics'
  },
  {
    id: 'top-pages',
    title: 'Top Pages by Views',
    description: 'Bar chart of most popular pages',
    icon: <BarChart3 className="h-6 w-6 text-orange-500" />,
    chartType: 'bar',
    dataSourceType: 'google-analytics',
    metrics: ['screenPageViews'],
    dimensions: ['pagePath'],
    dateRange: 'last 30 days',
    badge: 'Google Analytics'
  },
  {
    id: 'user-signups',
    title: 'User Signups Over Time',
    description: 'Track new user registrations',
    icon: <Users className="h-6 w-6 text-purple-500" />,
    chartType: 'line',
    dataSourceType: 'firestore',
    metrics: ['count'],
    dimensions: ['createdAt'],
    dateRange: 'last 30 days',
    badge: 'Firestore'
  },
  {
    id: 'collection-counts',
    title: 'Document Counts by Collection',
    description: 'Bar chart of documents per collection',
    icon: <Database className="h-6 w-6 text-indigo-500" />,
    chartType: 'bar',
    dataSourceType: 'firestore',
    metrics: ['documentCount'],
    dimensions: ['collection'],
    dateRange: 'all time',
    badge: 'Firestore'
  }
]

// Placeholder components for the different modes
const QuickActions: React.FC<{ dataSources: DataSource[]; onClose: () => void }> = ({ dataSources, onClose }) => {
  const { createChartFromTemplate } = useQuickChartCreation()
  const connectedSourceTypes = [...new Set(dataSources.filter(ds => ds.status === 'connected' || ds.status === 'active').map(ds => ds.type))]
  
  const availableTemplates = quickActionTemplates.filter(template => 
    template.dataSourceType === 'any' || connectedSourceTypes.includes(template.dataSourceType)
  )

  const handleTemplateClick = async (template: QuickActionTemplate) => {
    console.log('🚀 Quick action selected:', template.title)
    await createChartFromTemplate(template, dataSources)
    onClose() // Close the dialog after successful creation
  }

  if (availableTemplates.length === 0) {
    return (
      <div className="text-center py-12">
        <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No Data Sources Connected
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Connect your data sources to enable quick chart creation
        </p>
        <Button variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Connect Data Source
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {availableTemplates.map((template) => (
          <motion.div
            key={template.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card 
              className="cursor-pointer hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-600 transition-all group"
              onClick={() => handleTemplateClick(template)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg group-hover:bg-orange-100 dark:group-hover:bg-orange-900/50 transition-colors">
                    {template.icon}
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {template.badge}
                  </Badge>
                </div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                  {template.title}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {template.description}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Show connected data sources */}
      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Connected Data Sources ({dataSources.filter(ds => ds.status === 'connected' || ds.status === 'active').length})
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {dataSources.filter(ds => ds.status === 'connected' || ds.status === 'active').map((source) => (
            <div
              key={source.id}
              className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
            >
              {source.type === 'google-analytics' ? (
                <Activity className="h-4 w-4 text-green-600" />
              ) : (
                <Database className="h-4 w-4 text-green-600" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {source.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {source.type.replace('-', ' ')}
                </p>
              </div>
              <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                Connected
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const AIAssistant: React.FC<{ dataSources: DataSource[]; onClose: () => void }> = ({ dataSources, onClose }) => {
  const {
    intent,
    handleIntentChange,
    preview,
    isLoading,
    error,
    createChart,
    canCreateChart,
  } = useAiChartAssistant(dataSources, onClose)

  const customPalettes = useCustomPalettes()
  const isDarkMode = useTheme()
  const activeDefaultPaletteId = useActiveDefaultPaletteId()

  const renderPreview = () => {
    if (!preview || !preview.chartData) return null

    const config = normalizeChartData(preview.chartData as any, {
      height: 350,
      enableAdvancedFeatures: true,
      customPalettes,
      isDarkMode,
      globalDefaultPaletteId: activeDefaultPaletteId,
    })

    return <UniversalChartRenderer config={config} />
  }

  return (
    <div className="space-y-6 flex flex-col h-full">
      <div className="max-w-3xl mx-auto w-full">
        <div className="relative">
          <textarea
            value={intent}
            onChange={(e) => handleIntentChange(e.target.value)}
            placeholder="Describe what you want to visualize and get a live preview...&#10;&#10;e.g., Show me website traffic trends over the last 30 days as a line chart"
            className="w-full h-24 p-4 pr-28 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
          />
          <Button 
            className="absolute top-4 right-4"
            disabled={!canCreateChart}
            onClick={createChart}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Chart
          </Button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
        {isLoading && (
          <div className="text-center text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p>Generating preview...</p>
          </div>
        )}
        {error && !isLoading && (
          <div className="text-center text-red-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p className="font-medium">Error Generating Preview</p>
            <p className="text-sm">{error}</p>
          </div>
        )}
        {!isLoading && !error && preview && (
          <div className="w-full h-full">
            {renderPreview()}
          </div>
        )}
        {!isLoading && !error && !preview && (
          <div className="text-center text-gray-400 dark:text-gray-500">
            <p>Your chart preview will appear here.</p>
          </div>
        )}
      </div>
    </div>
  )
}

const ManualCreator: React.FC<{ dataSources: DataSource[]; onClose: () => void }> = ({ dataSources, onClose }) => {
  const {
    selectedDataSourceId,
    setSelectedDataSourceId,
    chartType,
    setChartType,
    selectedMetric,
    setSelectedMetric,
    selectedDimension,
    setSelectedDimension,
    dateRange,
    setDateRange,
    availableOptions,
    generatePreview,
    createChart,
    preview,
    isLoading,
    error,
    canGeneratePreview,
    canCreateChart,
  } = useManualChartCreator(dataSources, onClose)

  const customPalettes = useCustomPalettes()
  const isDarkMode = useTheme()
  const activeDefaultPaletteId = useActiveDefaultPaletteId()

  const renderPreview = () => {
    if (!preview || !preview.data) return null
    const config = normalizeChartData(preview as LocalChartData, {
      height: 350,
      enableAdvancedFeatures: true,
      customPalettes, isDarkMode, globalDefaultPaletteId: activeDefaultPaletteId,
    })
    return <UniversalChartRenderer config={config} />
  }

  const connectedSources = dataSources.filter(ds => ds.status === 'connected' || ds.status === 'active')

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-full">
      {/* Configuration Panel */}
      <div className="md:col-span-1 space-y-4 flex flex-col">
        <div className="space-y-4">
          <Select onValueChange={setSelectedDataSourceId} value={selectedDataSourceId || 'placeholder'}>
            <SelectTrigger><SelectValue placeholder="1. Select Data Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="placeholder">Select a data source</SelectItem>
              {connectedSources.map(ds => <SelectItem key={ds.id} value={ds.id}>{ds.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select onValueChange={setChartType as any} value={chartType} disabled={!selectedDataSourceId}>
            <SelectTrigger><SelectValue placeholder="2. Select Chart Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bar">Bar Chart</SelectItem>
              <SelectItem value="line">Line Chart</SelectItem>
              <SelectItem value="pie">Pie Chart</SelectItem>
            </SelectContent>
          </Select>
          
          <Select onValueChange={setSelectedMetric} value={selectedMetric || 'placeholder'} disabled={!availableOptions.metrics.length}>
            <SelectTrigger><SelectValue placeholder="3. Select a Metric" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="placeholder">Select a metric</SelectItem>
              {availableOptions.metrics.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          
          <Select onValueChange={setSelectedDimension} value={selectedDimension || 'placeholder'} disabled={!availableOptions.dimensions.length}>
            <SelectTrigger><SelectValue placeholder="4. Select a Dimension" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="placeholder">Select a dimension</SelectItem>
              {availableOptions.dimensions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select onValueChange={setDateRange} value={dateRange} disabled={!selectedDataSourceId}>
            <SelectTrigger><SelectValue placeholder="5. Select Date Range" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="last 7 days">Last 7 Days</SelectItem>
              <SelectItem value="last 30 days">Last 30 Days</SelectItem>
              <SelectItem value="last 90 days">Last 90 Days</SelectItem>
              <SelectItem value="last 12 months">Last 12 Months</SelectItem>
              <SelectItem value="all time">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-grow"></div>

        <div className="space-y-2">
          <Button onClick={generatePreview} disabled={!canGeneratePreview || isLoading} className="w-full">
            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            {isLoading ? 'Generating...' : 'Generate Preview'}
          </Button>
          <Button onClick={createChart} disabled={!canCreateChart} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Create Chart
          </Button>
        </div>
      </div>

      {/* Preview Panel */}
      <div className="md:col-span-2 flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed">
        {isLoading && <div className="text-center text-gray-500"><Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" /><p>Generating preview...</p></div>}
        {error && !isLoading && <div className="text-center text-red-500"><AlertCircle className="h-8 w-8 mx-auto mb-2" /><p className="font-medium">Error</p><p className="text-sm">{error}</p></div>}
        {!isLoading && !error && preview && preview.data && <div className="w-full h-full">{renderPreview()}</div>}
        {!isLoading && !error && !preview && <div className="text-center text-gray-400"><p>Your chart preview will appear here.</p></div>}
      </div>
    </div>
  )
}

const DataUploadCreator: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [uploadedData, setUploadedData] = useState<Record<string, any>[]>([])
  const [isUploadSectionCollapsed, setIsUploadSectionCollapsed] = useState(false)
  const [, addChart] = useAtom(addChartAtom)
  const { toast } = useToast()

  const handleDataChange = (data: Record<string, any>[]) => {
    setUploadedData(data)
    // Auto-collapse the upload section when data is first added
    if (data.length > 0 && !isUploadSectionCollapsed) {
      setIsUploadSectionCollapsed(true)
    }
  }

  const handleCreateChart = (data: Record<string, any>[], config: any) => {
    console.log('[UnifiedAIChartCreator] handleCreateChart called with config:', config)
    try {
      // Use processed chart data if available (for multi-series), otherwise transform raw data
      let chartData: any[]
      
      if (config.chartData && config.isMultiSeries) {
        // Use the pre-processed multi-series data
        chartData = config.chartData
      } else {
        // Transform raw data into the format expected by the chart system
        chartData = data.map(row => {
          const transformedRow: Record<string, string | number> = {}
          Object.entries(row).forEach(([key, value]) => {
            // Convert values to appropriate types
            if (typeof value === 'string' && !isNaN(parseFloat(value)) && isFinite(parseFloat(value))) {
              transformedRow[key] = parseFloat(value)
            } else {
              transformedRow[key] = String(value)
            }
          })
          return transformedRow
        })
      }

      // Create the chart using the standard chart creation system
      const chartConfig = {
        data: chartData,
        columns: Object.keys(chartData[0] || {}),
        chartType: config.chartType,
        xAxis: config.xAxis,
        yAxis: config.yAxis,
        title: config.title || `${config.yAxis} by ${config.xAxis}`,
        // Include color palette information in configuration
        configuration: {
          colorPalette: config.colorPalette
        },
        // Pass through multi-series information
        ...(config.isMultiSeries && {
          isMultiSeries: true,
          seriesInfo: config.seriesInfo,
          groupBy: config.groupBy
        })
      }
      
      console.log('[UnifiedAIChartCreator] Created chartConfig:', chartConfig)
      console.log('[UnifiedAIChartCreator] config.colorPalette:', config.colorPalette)
      
      const newChart = createStandardChartObject(chartConfig)

      // Add the chart to the store
      const createdChart = addChart(newChart)
      
      toast({
        title: 'Chart Created Successfully!',
        description: `${config.title || config.chartType + ' chart'} created with ${data.length} rows of data. Check the charts section to view it.`,
      })
      
      console.log('Created Chart:', createdChart)
      
      // Notify parent to refresh charts list
      if (onChartCreated) {
        onChartCreated()
      }
      
      // Close the dialog
      onClose()
    } catch (error) {
      console.error('Error creating chart:', error)
      toast({
        title: 'Error Creating Chart',
        description: 'There was an error creating your chart. Please try again.',
        variant: 'destructive'
      })
    }
  }

  const hasData = uploadedData.length > 0

  return (
    <div className="h-full flex flex-col">
      {/* Collapsible Upload Section */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          {/* <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Upload Your Data
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Upload a CSV file or paste your data to create charts with custom datasets
            </p>
          </div> */}
          {hasData && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsUploadSectionCollapsed(!isUploadSectionCollapsed)}
              className="ml-4 flex items-center gap-2"
            >
              {isUploadSectionCollapsed ? (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show Upload Options
                </>
              ) : (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Hide Upload Options
                </>
              )}
            </Button>
          )}
        </div>
        
        <AnimatePresence>
          {(!hasData || !isUploadSectionCollapsed) && (
            <motion.div
              initial={{ height: 'auto', opacity: 1 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="mb-6">
                <DataTableViewer
                  initialData={uploadedData}
                  onDataChange={handleDataChange}
                  enableSmartFormatting={true}
                  onCreateChart={handleCreateChart}
                  className="h-auto"
                  showHeaderOnly={true}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Data Display Area */}
      <div className="flex-1 min-h-0">
        <DataTableViewer
          initialData={uploadedData}
          onDataChange={handleDataChange}
          enableSmartFormatting={true}
          onCreateChart={handleCreateChart}
          className="h-full"
          showTableOnly={true}
        />
      </div>
    </div>
  )
}

export const UnifiedAIChartCreator: React.FC<UnifiedAIChartCreatorProps> = ({
  isOpen,
  onClose,
  dataSources,
  onChartCreated
}) => {
  const [mode, setMode] = useState<CreationMode>('quick')

  const modeConfig = {
    quick: {
      label: 'Quick Actions',
      icon: <Zap className="h-4 w-4" />,
      description: 'One-click chart creation'
    },
    ai: {
      label: 'AI Assistant', 
      icon: <Sparkles className="h-4 w-4" />,
      description: 'Natural language powered'
    },
    manual: {
      label: 'Manual Setup',
      icon: <Settings className="h-4 w-4" />,
      description: 'Full control and customization'
    },
    upload: {
      label: 'Upload Data',
      icon: <Upload className="h-4 w-4" />,
      description: 'CSV files and pasted data'
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] lg:w-[90vw] xl:w-[85vw] max-w-[1400px] h-[85vh] p-0 !max-w-none sm:!max-w-none flex flex-col">
        {/* Fixed Header */}
        <div className="flex-shrink-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-2xl flex items-center gap-3">
              <BarChart3 className="h-6 w-6 text-orange-500" />
              Create a Chart
            </DialogTitle>
          </DialogHeader>

          {/* Fixed Tab Bar */}
          <div className="px-6 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
              {(Object.keys(modeConfig) as CreationMode[]).map((modeKey) => (
                <Button
                  key={modeKey}
                  variant={mode === modeKey ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setMode(modeKey)}
                  className={`flex-1 flex items-center gap-2 ${
                    mode === modeKey 
                      ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100' 
                      : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {modeConfig[modeKey].icon}
                  <div className="text-left">
                    <div className="font-medium">{modeConfig[modeKey].label}</div>
                    <div className={`text-xs ${
                      mode === modeKey 
                        ? 'text-gray-600 dark:text-gray-400' 
                        : 'text-gray-500 dark:text-gray-400 opacity-70'
                    }`}>{modeConfig[modeKey].description}</div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto overflow-x-hidden p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="min-h-full"
              >
                {mode === 'quick' && <QuickActions dataSources={dataSources} onClose={onClose} />}
                {mode === 'ai' && <AIAssistant dataSources={dataSources} onClose={onClose} />}
                {mode === 'manual' && <ManualCreator dataSources={dataSources} onClose={onClose} />}
                {mode === 'upload' && <DataUploadCreator onClose={onClose} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
} 